import {
    defaultSoleConfiguration,
    defaultSoleCorrections,
    defaultSoleInformations,
    defaultSoleMorphing,
    defaultSoleParameters,
} from '@/domains/sole/services/utils/defaultValues';
import { TemplateFittingArches } from '@/domains/sole/services/templateFittingArches';
import FootSideEnum from '@/domains/scan/typescript/enums/FootSideEnum';
import { defineStore } from 'pinia';
import { useLocalStorage } from '@vueuse/core';
import { computed, ref } from 'vue';
import type SoleCorrectionElementsType from '@/domains/sole/typescript/types/SoleCorrectionElementsType';
import type SoleConfigurationElementInterface from '@/domains/sole/typescript/interfaces/SoleConfigurationElementInterface';
import type SoleConfigurationElementCategoryInterface from '@/domains/sole/typescript/interfaces/SoleConfigurationElementCategoryInterface';
import type SoleConfigurationElementCategoryListInterface from '@/domains/sole/typescript/interfaces/SoleConfigurationElementCategoryListInterface';
import type SoleMorphingInterface from '@/domains/sole/typescript/interfaces/SoleMorphingInterface';
import type SoleCorrectionsInterface from '@/domains/sole/typescript/interfaces/SoleCorrectionsInterface';
import type SoleParametersInterface from '@/domains/sole/typescript/interfaces/SoleParametersInterface';
import type SoleInformationInterface from '@/domains/sole/typescript/interfaces/SoleInformationInterface';
import type SoleConfigurationInterface from '@/domains/sole/typescript/interfaces/SoleConfigurationInterface';
import type TemplateFittingEnum from '@/domains/sole/typescript/enums/TemplateFittingEnum';
import type SoleConfigurationElementFavoriteInterface from '@/domains/sole/typescript/interfaces/SoleConfigurationElementFavoriteInterface';

const RHINO_ID_STORAGE_KEY = 'rhinoId';

export default defineStore('sole', () => {
    const rhinoId = useLocalStorage<string>(RHINO_ID_STORAGE_KEY, null);

    const soleInformations = ref<SoleInformationInterface>({
        ...defaultSoleInformations,
    });
    const soleConfiguration = ref<SoleConfigurationInterface>({ ...defaultSoleConfiguration });

    const soleParametersLeft = ref<SoleParametersInterface>({
        ...defaultSoleParameters,
    });

    const soleParametersRight = ref<SoleParametersInterface>({
        ...defaultSoleParameters,
    });

    const soleCorrectionsLeft = ref<SoleCorrectionsInterface>({
        ...defaultSoleCorrections,
    });

    const soleCorrectionsRight = ref<SoleCorrectionsInterface>({
        ...defaultSoleCorrections,
    });

    const soleMorphingLeft = ref<SoleMorphingInterface>({
        ...defaultSoleMorphing,
    });

    const soleMorphingRight = ref<SoleMorphingInterface>({
        ...defaultSoleMorphing,
    });

    function applyTemplateFitting(templateFitting: TemplateFittingEnum) {
        if (soleInformations.value.templateFitting === templateFitting) {
            return;
        }
        soleInformations.value.templateFitting = templateFitting;
        soleParametersLeft.value = TemplateFittingArches.clampEditableArches(templateFitting, soleParametersLeft.value);
        soleParametersRight.value = TemplateFittingArches.clampEditableArches(
            templateFitting,
            soleParametersRight.value,
        );
    }

    const templateFittedSoleParametersLeft = computed(() =>
        TemplateFittingArches.zeroTemplateFittedArches(
            soleInformations.value.templateFitting,
            soleParametersLeft.value,
        ),
    );

    const templateFittedSoleParametersRight = computed(() =>
        TemplateFittingArches.zeroTemplateFittedArches(
            soleInformations.value.templateFitting,
            soleParametersRight.value,
        ),
    );

    function getSoleParameters(footSide: FootSideEnum): SoleParametersInterface {
        return footSide === FootSideEnum.Left ? soleParametersLeft.value : soleParametersRight.value;
    }

    function setInterArchPoint(footSide: FootSideEnum, point: string) {
        const soleParameters = TemplateFittingArches.clampEditableArches(soleInformations.value.templateFitting, {
            ...getSoleParameters(footSide),
            interArchPoint: point,
        });
        if (footSide === FootSideEnum.Left) {
            soleParametersLeft.value = soleParameters;
        } else {
            soleParametersRight.value = soleParameters;
        }
    }

    function setArchFromScan(footSide: FootSideEnum, arch: { height: number; position?: number }) {
        const soleParameters = getSoleParameters(footSide);
        if (arch.position !== undefined) {
            soleParameters.internalArchSummitsPosition = arch.position;
        }
        if (!TemplateFittingArches.isInternalArchTemplateFitted(soleInformations.value.templateFitting)) {
            soleParameters.internalArch = `${arch.height}`;
        }
    }

    const hasSizeChanged = ref(false);

    function acknowledgeSizeChange() {
        hasSizeChanged.value = false;
    }

    const catalogElements = ref<SoleConfigurationElementInterface[]>();
    const soleCorrectionElementList = ref<SoleCorrectionElementsType[]>([]);
    const favoriteElements = ref<SoleConfigurationElementFavoriteInterface[]>([]);

    const selected3DItemId = ref<number>(-1);

    const filteredCategoriesWithElements = ref<SoleConfigurationElementCategoryListInterface[]>();

    const soleHash = ref<string>();
    const lastComputeHash = ref<string>();
    const isHashDigestEnabled = ref<boolean>(false);
    const has3DChanged = ref(false);
    // Bumped whenever the 3D scene moves an element. The compute hash reads element
    // transforms straight from the scene, which is not reactive: this counter is what
    // invalidates the hash. See useSoleHelper.markElementSceneChanged (#1042).
    const elementSceneVersion = ref(0);

    const elementCategories = computed<SoleConfigurationElementCategoryInterface[] | undefined>(() => {
        if (catalogElements.value === undefined) {
            return undefined;
        }

        const elementsCategories: SoleConfigurationElementCategoryInterface[] = catalogElements.value.map(
            (element) => element.category,
        );

        return elementsCategories.reduce((uniqueCategories, value) => {
            const categoryExists = uniqueCategories.some((cat) => cat.code === value.code);
            if (!categoryExists) {
                return [...uniqueCategories, value];
            }
            return uniqueCategories;
        }, [] as SoleConfigurationElementCategoryInterface[]);
    });

    const allCategoriesWithElements = computed<SoleConfigurationElementCategoryListInterface[] | undefined>(() => {
        if (catalogElements.value === undefined) {
            return undefined;
        }

        if (elementCategories.value === undefined) {
            return undefined;
        }

        const loadedElements = catalogElements.value;
        const elementsByCategory: SoleConfigurationElementCategoryListInterface[] = elementCategories.value.map(
            (category) => {
                const elements = loadedElements.filter((element) => {
                    return element.category.code === category.code;
                });

                return { ...category, elements };
            },
        );

        return elementsByCategory;
    });

    const selectedElementCategoryCode = computed<string>(() => {
        if (selected3DItemId.value > 0) {
            const elementId = soleCorrectionElementList.value.find(
                (element) => element.sceneId === selected3DItemId.value,
            )?.elementId;
            if (elementId) {
                return (
                    catalogElements.value?.find((catalogElement) => catalogElement.id === elementId)?.category.code ||
                    ''
                );
            }
        }
        return '';
    });

    function $reset() {
        console.info('reset sole store');
        soleInformations.value = {
            ...defaultSoleInformations,
        };
        soleParametersLeft.value = {
            ...defaultSoleParameters,
        };
        soleParametersRight.value = {
            ...defaultSoleParameters,
        };
        soleCorrectionsLeft.value = {
            ...defaultSoleCorrections,
        };
        soleCorrectionsRight.value = {
            ...defaultSoleCorrections,
        };
        soleConfiguration.value = {
            ...defaultSoleConfiguration,
        };
        soleMorphingLeft.value = {
            ...defaultSoleMorphing,
        };
        soleMorphingRight.value = {
            ...defaultSoleMorphing,
        };
        hasSizeChanged.value = false;
        rhinoId.value = null;
        soleCorrectionElementList.value = [];
        soleHash.value = undefined;
        lastComputeHash.value = undefined;
        isHashDigestEnabled.value = false;
        has3DChanged.value = false;
        elementSceneVersion.value = 0;
        // The selected element is order-scoped, the catalogue and the favourites are
        // office-user-scoped: all of them belong to whoever was logged in, and resetAllStores()
        // relies on this function to leave nothing behind. Both lists are refetched on mount.
        selected3DItemId.value = -1;
        catalogElements.value = undefined;
        favoriteElements.value = [];
        filteredCategoriesWithElements.value = undefined;
        // Last, and on purpose: useLocalStorage flushes on 'pre', so the key would still be there
        // for a caller tearing the page down synchronously (ScanView.saveAndReload). Storage can
        // be denied, and a throw here must not leave the in-memory patient data above untouched.
        localStorage.removeItem(RHINO_ID_STORAGE_KEY);
    }

    return {
        rhinoId,
        soleParametersLeft,
        soleParametersRight,
        soleCorrectionsLeft,
        soleCorrectionsRight,
        soleMorphingLeft,
        soleMorphingRight,
        templateFittedSoleParametersLeft,
        templateFittedSoleParametersRight,
        applyTemplateFitting,
        setInterArchPoint,
        setArchFromScan,
        hasSizeChanged,
        acknowledgeSizeChange,
        filteredCategoriesWithElements,
        catalogElements,
        favoriteElements,
        allCategoriesWithElements,
        selectedElementCategoryCode,
        soleCorrectionElementList,
        soleInformations,
        soleConfiguration,
        selected3DItemId,
        soleHash,
        lastComputeHash,
        isHashDigestEnabled,
        has3DChanged,
        elementSceneVersion,
        $reset,
    };
});
